import React, { useState } from 'react';
import { Field, Formik, Form } from 'formik';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  Toggler,
  ComboBox,
  Button,
  TextInput,
  Radio,
  LoadingIndicator,
} from '@components';
import { MAIANDocs, MAIANInitialValues, MAIANSchema } from '@schemas';
import { enviaFormRequest } from '@store/user/actions';

import { constantesRequerimentos } from '@utils';
import * as yup from 'yup';

import { ModalUpload } from './components/ModalUpload';
import { validaUpload } from './utils/validaUpload';
import { DadosUsuario } from './components/DadosUsuario';
import {
  PesquisaTendencia,
  opcoesMotivoRetiradaPesquisaTendencia,
  pesquisaTendenciaInitialValues,
} from './components/PesquisaTendencia';

const { TEMPORARIA_FIXO, TEMPORARIA_VARIAVEL } = constantesRequerimentos;

const Anexos = ({ resetAnexos }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);

  return (
    <>
      <ModalUpload modalIsOpen={modalIsOpen} setModalIsOpen={setModalIsOpen} />
      {MAIANDocs.commons.map((anexo) => (
        <div className="row" key={anexo.name}>
          <div className="col-md-12 col-lg-6">
            <Field
              className="mb-3"
              name={anexo.name}
              type="file"
              label={anexo.label}
              accept="image/*,.pdf"
              key={resetAnexos}
            >
              {({ form }) => {
                return (
                  <TextInput
                    key={resetAnexos}
                    className="mb-3"
                    name={anexo.name}
                    type="file"
                    label={anexo.label}
                    accept="image/*,.pdf"
                    multiple
                    onChange={(e) => {
                      return validaUpload({
                        anexo,
                        e,
                        form,
                        setModalIsOpen,
                      });
                    }}
                  />
                );
              }}
            </Field>
          </div>
        </div>
      ))}
    </>
  );
};

export const MAIAN = ({ title, reference }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { requestDone, saldoDevedor } = useSelector(
    (state) => state.emprestimo.limite
  );

  const [showForm, setShowForm] = useState(false);

  const pesquisaTendenciaCondicionalSchema = yup.object({
    motivo_temp: yup.string().when('parcelaAVista', {
      is: 'Sim',
      then: yup
        .string()
        .oneOf(opcoesMotivoRetiradaPesquisaTendencia.map((o) => o.id))
        .required('Atenção, selecione o motivo.'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

    instituicao_temp: yup.string().when(['motivo_temp', 'parcelaAVista'], {
      is: (motivoTemp, parcelaAVista) => {
        return (
          parcelaAVista === 'Sim' &&
          motivoTemp === 'Investir em outra instituição'
        );
      },
      then: (field) => field.trim().required('Atenção, informe a instituição.'),
      otherwise: (field) => field.notRequired().nullable(),
    }),

    outro_temp: yup.string().when(['motivo_temp', 'parcelaAVista'], {
      is: (motivoTemp, parcelaAVista) => {
        return parcelaAVista === 'Sim' && motivoTemp === 'Outros';
      },
      then: (field) => field.trim().required('Atenção, especifique.'),
      otherwise: (field) => field.notRequired().nullable(),
    }),

    pesquisaTendencia: yup.string().when('parcelaAVista', {
      is: 'Sim',
      then: yup.string().required('Atenção, responda o questionário.'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  });

  return !requestDone ? (
    <LoadingIndicator />
  ) : (
    <div className="card">
      <div className="card-body">
        {showForm ? (
          <Formik
            validateOnMount
            initialValues={{
              ...MAIANInitialValues,
              ...pesquisaTendenciaInitialValues,
            }}
            validationSchema={MAIANSchema(saldoDevedor).concat(
              pesquisaTendenciaCondicionalSchema
            )}
            onSubmit={(values) => {
              dispatch(
                enviaFormRequest({
                  campos: values,
                  formulario: reference,
                  navigate,
                })
              );
            }}
          >
            {({
              values,
              handleSubmit,
              isValid,
              setFieldValue,
              setFieldTouched,
            }) => {
              return (
                <Form>
                  <h3 className="fw-bold ts-20 mb-32">
                    Opção de Regime de Tributação
                  </h3>

                  <p className="mb-32">
                    Escolha o Regime de Tributação do Imposto de Renda que será
                    calculado no seu requerimento com base na Lei nº 11.053 de
                    2004, alterada pela Lei nº 14.803 de 2024. Essa Lei permite
                    aos participantes de planos de previdência a escolha de seu
                    regime de tributação na ocasião da obtenção do benefício ou
                    do primeiro resgate dos valores acumulados.
                  </p>

                  <p className="mb-32">
                    <strong>Formalizo a minha opção pelo Regime: </strong>
                  </p>

                  <div className="row">
                    <Field
                      className="mb-5 ms-3"
                      name="tipoRegime"
                      component={Radio}
                      value="Progressivo"
                      label="Progressivo: os valores recebidos da Forluz serão tributados conforme tabela para cálculo do Imposto de Renda (IR), o que significa que o desconto do imposto na fonte terá alíquotas variáveis conforme o montante recebido, ou seja, quanto maior o valor, maior a alíquota. Atualmente, as alíquotas variam de 0% a 27,5%. Nesse regime poderão ocorrer deduções mensais e ajustes na declaração anual de Imposto de Renda. "
                    />
                  </div>

                  <div className="row">
                    <Field
                      className="mb-3 ms-3"
                      name="tipoRegime"
                      component={Radio}
                      value="Regressivo"
                      label="Regressivo: neste caso, o IR pago no recebimento da Forluz vai variar entre 35% e 10% de acordo com o tempo de aporte de recursos ao plano, ou seja, quanto maior o tempo, menor a alíquota. A tributação será exclusivamente na fonte, ou seja, não serão realizados ajustes anuais na declaração de Imposto de Renda. "
                    />
                  </div>

                  <p className="mb-32 mt-32">
                    <strong>Declarações</strong>
                  </p>

                  <div className="row">
                    <Field
                      className="mb-3"
                      name="aceite"
                      component={Toggler}
                      label="Declaro ciência de que a opção marcada é IRRETRATÁVEL, ou seja, saques posteriores ou resgates terão a tributação calculada de acordo com essa escolha."
                    />
                  </div>

                  <div className="row">
                    <Field
                      className="mb-3"
                      name="declaracaoTipoRegime"
                      component={Toggler}
                      label="Declaro ciência de que caso já tenha feito algum resgate, será desconsiderada a opção marcada acima e será aplicado o regime escolhido no primeiro resgate. "
                    />
                  </div>

                  <hr className="mt-32 mt-xs-0 mb-32" />

                  <h3 className="fw-bold ts-20 mb-32">{title} </h3>

                  <p className="mb-4">
                    Venho, por meio deste, requerer à Fundação Forluminas de
                    Seguridade Social - FORLUZ o benefício de Melhoria de
                    Aposentadoria por Invalidez – MAI, cujo pagamento será
                    realizado conforme minhas opções de acordo com o Regulamento
                    do Plano Misto de Benefícios Previdenciários da Forluz
                    (Plano B), as normas vigentes e aquelas que venham a
                    alterá-las ou substituí-las. Para tanto, anexo a
                    documentação necessária para a finalidade.
                  </p>
                  <div className="row">
                    <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="parcelaAVista"
                        component={ComboBox}
                        tooltip="Retirada antecipada de até 50% do saldo de conta; acarreta redução proporcional no valor do benefício. Haverá incidência de Imposto de Renda conforme regime de tributação escolhido e o saldo devedor do empréstimo será descontado (antecipação do prazo) na mesma proporção da sua retirada."
                        options={['Sim', 'Não'].map((x) => {
                          return { value: x, label: x };
                        })}
                        label="Parcela à Vista"
                        onChange={(op) => {
                          if (op.value !== 'Sim') {
                            setFieldValue('retiradaAVista', '', false);
                            setFieldValue('motivo_temp', '', false);
                            setFieldValue('instituicao_temp', '', false);
                            setFieldValue('outro_temp', '', false);
                            setFieldValue('pesquisaTendencia', '', false);

                            setFieldTouched('retiradaAVista', false, false);
                            setFieldTouched('motivo_temp', false, false);
                            setFieldTouched('instituicao_temp', false, false);
                            setFieldTouched('outro_temp', false, false);
                            setFieldTouched('pesquisaTendencia', false, false);
                          }
                        }}
                      />
                    </div>
                    <div className="col-md-12 col-lg-6">
                      <Field
                        disabled={!(values.parcelaAVista === 'Sim')}
                        className="mb-3"
                        name="retiradaAVista"
                        label="Percentual retirada à vista"
                        component={TextInput}
                        type="number"
                        min="0"
                        max="50"
                      />
                    </div>
                  </div>

                  {values.parcelaAVista === 'Sim' && <PesquisaTendencia />}

                  {!!saldoDevedor && values.parcelaAVista === 'Sim' && (
                    <div className="row">
                      <div className="col-md-12 col-lg-6">
                        <Field
                          className="mb-3"
                          name="boletoEmprestimo"
                          component={ComboBox}
                          tooltip="Boleto para quitação integral do saldo devedor remanescente será encaminhado por e-mail do endereço de e-mail cadastrado com vencimento no último dia útil do mês seguinte ao requerimento de benefício."
                          options={['Sim', 'Não'].map((x) => {
                            return { value: x, label: x };
                          })}
                          label="Boleto para quitação de empréstimo"
                          onChange={() => {
                            setFieldValue('abatimentoDoEmprestimo', '', false);
                          }}
                        />
                      </div>

                      {values.boletoEmprestimo === 'Não' && (
                        <div className="col-md-12 col-lg-6">
                          <Field
                            className="mb-3"
                            name="abatimentoDoEmprestimo"
                            component={ComboBox}
                            tooltip="Antecipação:	Modalidade de pagamento em que prazo final do contrato é reduzido ( abatimento das últimas parcelas) e o valor da parcela mantido. Nesta modalidade o montante de juros é reduzido em função do encurtamento do contrato. Amortização:	Modalidade de pagamento em que o valor da parcela é reduzido e o prazo contratado mantido. "
                            options={['Antecipação', 'Amortização'].map((x) => {
                              return { value: x, label: x };
                            })}
                            label="Abatimento do empréstimo"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-12 col-lg-6 mb-3">
                      <p className="label mb-3">Opção benefício</p>
                      <Field
                        className="mb-3"
                        name="opcaoBeneficio"
                        component={Radio}
                        tooltip="Benefício pago ao participante pelo prazo determinado pelo participante no requerimento. O saldo de conta será esgotado no encerramento do benefício."
                        value={TEMPORARIA_FIXO}
                        label={TEMPORARIA_FIXO}
                        onChange={() => {
                          setFieldValue('percentualRetiradaMensal', '', false);
                          setFieldTouched(
                            'percentualRetiradaMensal',
                            '',
                            false
                          );
                        }}
                      />
                      <Field
                        className="mb-3"
                        name="opcaoBeneficio"
                        component={Radio}
                        tooltip="Benefício pago ao participante pelo prazo determinado pelo participante no requerimento. O saldo de conta será esgotado no encerramento do benefício."
                        value={TEMPORARIA_VARIAVEL}
                        label={TEMPORARIA_VARIAVEL}
                        onChange={() => {
                          setFieldValue('prazo', '', false);
                          setFieldTouched('prazo', '', false);
                        }}
                      />
                    </div>
                  </div>

                  {[TEMPORARIA_FIXO].includes(values.opcaoBeneficio) && (
                    <div className="row">
                      <div className="col-md-12 col-lg-6 mb-3">
                        <Field
                          label="Prazo"
                          name="prazo"
                          component={ComboBox}
                          options={['10 anos', '15 anos', '20 anos'].map(
                            (x) => {
                              return { value: x, label: x };
                            }
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {[TEMPORARIA_VARIAVEL].includes(values.opcaoBeneficio) && (
                    <div className="row">
                      <div className="col-md-12 col-lg-6 mb-3">
                        <Field
                          label="Percentual retirada mensal"
                          name="percentualRetiradaMensal"
                          component={TextInput}
                          min={0.1}
                          max={1}
                          type="number"
                        />
                      </div>
                    </div>
                  )}

                  {[TEMPORARIA_VARIAVEL].includes(values.opcaoBeneficio) && (
                    <div className="row">
                      <p className="mb-4">
                        Ao optar pela Renda Temporária em Valor Variável,
                        declaro ter pleno conhecimento de que:
                        <ol>
                          <li>
                            A rentabilidade mensal aplicável sobre o saldo da
                            minha conta após entrar em gozo de benefício será
                            aquela auferida sobre os recursos garantidores do
                            perfil da minha escolha. Caso não opte pelo perfil,
                            a Diretoria definirá o pefil no qual serão
                            investidos os respectivos recursos. O saldo da minha
                            conta dependerá da rentabilidade obtida e do valor
                            do benefício.
                          </li>
                          <li>
                            Não há garantia de rentabilidade mínima, podendo,
                            inclusive, ocorrer rentabilidade negativa.
                          </li>
                          <li>
                            O valor do benefício dependerá do saldo da conta e
                            do percentual escolhido e será recalculado
                            anualmente.
                          </li>
                          <li>
                            O benefício se encerrará quando se exaurir o saldo
                            existente em minha conta.
                          </li>
                        </ol>
                      </p>
                      <Field
                        className="mb-3"
                        name="consentimentoBeneficio"
                        component={Toggler}
                        label="Consentimento opção de benefício"
                      />
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="adiantamentoQuinzenal"
                        component={ComboBox}
                        tooltip="Antecipação de 35% do valor bruto, pago no dia 15 de cada mês."
                        label="Adiantamento quinzenal?"
                        options={['Sim', 'Não'].map((x) => {
                          return { value: x, label: x };
                        })}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="acessoContraCheque"
                        label="Acesso ao contracheque via"
                        component={ComboBox}
                        options={['Portal', 'E-mail e portal'].map((x) => {
                          return { value: x, label: x };
                        })}
                      />
                    </div>

                    {/* <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="acessoJornalForluz"
                        component={ComboBox}
                        label="Acesso Jornal Forluz"
                        options={['Digital', 'Impresso'].map((x) => {
                          return { value: x, label: x };
                        })}
                      />
                    </div> */}
                  </div>

                  <div className="row">
                    <p className="mb-3">
                      Declaro estar ciente de que a Forluz operacionalizará os
                      descontos decorrentes de eventual relacionamento com as
                      Entidades Externas (ABCF; AEA; AIC; ANAPAR; CECREMEC;
                      CEMIG SAÚDE; CLIC; GREMIG; SEGURO DE VIDA; SINDICATO JUIZ
                      DE FORA; SINDIELETRO; SINDSUL), por meio de descontos no
                      meu benefício para repasse à(s) Entidade(s), os quais
                      poderão ser visualizados mensalmente no contracheque.
                      Declaro estar ciente, ainda, de que devo contatar
                      diretamente a Entidade à qual sou filiado para o
                      cancelamento das cobranças e/ou ressarcimento de qualquer
                      valor.
                    </p>
                    <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="consentimentoDescontoFolha"
                        component={Toggler}
                        label="Consentimento descontos em folha (obrigatório)"
                      />
                    </div>
                  </div>

                  <div className="row">
                    <Field
                      className="mb-3"
                      name="consentimentoAjustePagamento"
                      component={Toggler}
                      createTermsField
                      label="Autorizo que, em caso de falecimento, créditos e débitos oriundos de resíduos e adiantamentos anteriores ao óbito, ou valores processados indevidamente após essa data, sejam descontados ou creditados em favor dos meus beneficiários de renda continuada por morte do plano de benefícios."
                    />
                  </div>

                  <div className="row">
                    <p className="mb-3">
                      Com base na Lei nº 13.709/2018 (Lei Geral de Proteção de
                      Dados Pessoais), autorizo o tratamento dos meus dados
                      pessoais e dados pessoais sensíveis para fins relacionados
                      ao meu vínculo com a FORLUZ.
                    </p>
                    <div className="col-md-12 col-lg-6">
                      <Field
                        className="mb-3"
                        name="consentimentoDadosSensiveis"
                        component={Toggler}
                        label="Consentimento dados sensiveis (obrigatório)"
                      />
                    </div>

                    <Field
                      className="mb-3"
                      name="aceiteInss"
                      component={Toggler}
                      createTermsField
                      label="Estou ciente que pessoa aposentada pela Previdência Oficial (INSS) e acometida por moléstia grave faz jus à isenção de Imposto de Renda. Este pedido poderá ser solicitado à Forluz a qualquer tempo através do formulário Isenção de Imposto de Renda disponível no menu Requerimentos. Se deferida, a isenção será processada no mês seguinte ao requerimento. "
                    />
                  </div>

                  <Anexos resetAnexos={values.opcaoBeneficio} />

                  <Field
                    className="mb-3"
                    name="declaracaoVeracidade"
                    component={Toggler}
                    createTermsField
                    label="Declaro que as informações e os documentos por mim apresentados à Forluz são verdadeiros, estando ciente de que, caso não sejam autênticos, estarei sujeito às consequências civis e criminais cabíveis, além de autorizar a Forluz a anular o ato praticado ou o benefício concedido."
                  />

                  <hr className="mt-32 mt-xs-0 mb-32" />

                  <div className="row">
                    <div className="col-12 col-md-6 col-xl-4 ms-0 ms-md-auto">
                      <div className="d-block d-md-flex align-items-center">
                        <Button
                          disabled={!isValid}
                          className="ms-0 ms-md-3"
                          onClick={handleSubmit}
                        >
                          Continuar
                        </Button>
                      </div>
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        ) : (
          <DadosUsuario
            onClick={() => {
              window.scroll(0, 0);
              setShowForm(true);
            }}
            dadosBancarios
          />
        )}
      </div>
    </div>
  );
};
